(() => {
  const runtime = window.BogerSections;
  if (!runtime) return;

  const setText = (node, value) => {
    if (node && node.textContent !== value) node.textContent = value;
  };
  const setHidden = (node, hidden) => {
    if (node && node.hidden !== hidden) node.hidden = hidden;
  };
  const setClassState = (node, className, active) => {
    if (node && node.classList.contains(className) !== active) node.classList.toggle(className, active);
  };
  const nodeTouchesSelector = (node, selector, includeDescendants = false) => {
    if (!(node instanceof Element)) return false;
    return node.matches(selector) || Boolean(node.closest(selector)) || (includeDescendants && Boolean(node.querySelector(selector)));
  };
  const mutationsTouchSelector = (mutations, selector) => mutations.some(mutation => (
    nodeTouchesSelector(mutation.target, selector)
    || [...mutation.addedNodes].some(node => nodeTouchesSelector(node, selector, true))
    || [...mutation.removedNodes].some(node => nodeTouchesSelector(node, selector, true))
  ));

  runtime.register('mobile-menu', '[data-site-header]', (root, { signal }) => {
    const button = root.querySelector('.menu-button');
    const menu = root.querySelector('.site-menu');
    if (!button || !menu) return;
    const close = (restoreFocus = false) => {
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-label', 'Open menu');
      menu.classList.remove('is-open');
      document.body.classList.remove('menu-open');
      if (restoreFocus) button.focus();
    };
    button.addEventListener('click', () => {
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      button.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      menu.classList.toggle('is-open', open);
      document.body.classList.toggle('menu-open', open);
    }, { signal });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && button.getAttribute('aria-expanded') === 'true') close(true);
    }, { signal });
    menu.addEventListener('click', event => {
      if (event.target.closest('a')) close();
    }, { signal });
    window.matchMedia('(min-width: 1181px)').addEventListener('change', event => {
      if (event.matches) close();
    }, { signal });
    return () => close();
  });

  runtime.register('mobile-shop-cta', '[data-mobile-shop-cta]', (root, { signal }) => {
    if (!window.matchMedia('(max-width: 767px)').matches || !('IntersectionObserver' in window)) return;
    const blockers = new Map();
    const setSuppressed = () => setClassState(root, 'is-suppressed', [...blockers.values()].some(Boolean));
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => blockers.set(entry.target, entry.isIntersecting));
      setSuppressed();
    }, { threshold: 0.08, rootMargin: '0px 0px -8% 0px' });
    document.querySelectorAll('main form, [data-primary-shop-link], .site-footer').forEach(element => {
      blockers.set(element, false);
      observer.observe(element);
    });
    const overlaySelector = '#shopify-pc__banner, .shopify-pc__banner__dialog, #ShopifyChat, [data-testid="consent-banner"]';
    const checkShopifyOverlays = () => {
      const overlay = document.querySelector(overlaySelector);
      const visible = Boolean(overlay && overlay.getClientRects().length);
      blockers.set(document.body, visible);
      setSuppressed();
    };
    const mutationObserver = new MutationObserver(mutations => {
      if (mutationsTouchSelector(mutations, overlaySelector)) checkShopifyOverlays();
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'style'] });
    checkShopifyOverlays();
    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
      root.classList.remove('is-suppressed');
    };
  });

  runtime.register('shopify-product-form', '[data-product-root]', (root, { signal }) => {
    const form = root.querySelector('.bp-product-form');
    const select = root.querySelector('[data-variant-select]');
    const variantField = form?.elements.namedItem('id');
    const price = root.querySelector('[data-product-price-current]');
    const comparePrice = root.querySelector('[data-product-price-compare]');
    const discount = root.querySelector('[data-product-discount]');
    const addButtons = [...root.querySelectorAll('[data-add-to-cart]')];
    const primaryAdd = addButtons.find(button => !button.hasAttribute('data-sticky-add-to-cart'));
    const sticky = root.querySelector('[data-sticky-buy]');
    const stickyPrice = root.querySelector('[data-sticky-price]');
    const bundleRequired = root.hasAttribute('data-valley-walk-set');
    const bundleReadiness = form?.querySelector('[data-bundle-readiness]');
    const bundleSelectionSummary = form?.querySelector('[data-bundle-selection-summary]');
    const serverVariantId = root.dataset.serverVariantId || '';
    const serverVariantAvailable = root.dataset.serverVariantAvailable === 'true';
    const bundleChoiceNames = ['Harness Size', 'Harness Color', 'Treat Pouch Color', 'Bowl Color'];
    const bundlePropertyNames = [...bundleChoiceNames, 'Product 4'];
    let bundleReady = !bundleRequired;

    const enhanceSimpleBundles = () => {
      const widget = form?.querySelector('#simple-bundles-io-options');
      if (!widget) {
        bundleReady = !bundleRequired;
        // Simple Bundles reads the native variant field while it mounts. Keep the
        // server-rendered ID available for App startup; disabled buttons and the
        // submit-time bundleReady gate keep the form fail-closed until validated.
        if (bundleRequired && !select && variantField && variantField.value !== serverVariantId) variantField.value = serverVariantId;
        if (bundleReadiness) {
          setHidden(bundleReadiness, false);
          setText(bundleReadiness, 'Loading set options…');
        }
        return;
      }
      const sizeSelect = widget.querySelector('select#harness-size[name="properties[Harness Size]"]');
      if (sizeSelect && sizeSelect.dataset.bpSizeOrder !== 's-m-l') {
        const selectedValue = sizeSelect.value;
        const sizeOrder = ['S', 'M', 'L'];
        [...sizeSelect.options]
          .sort((left, right) => sizeOrder.indexOf(left.value) - sizeOrder.indexOf(right.value))
          .forEach(option => sizeSelect.append(option));
        sizeSelect.value = selectedValue;
        sizeSelect.dataset.bpSizeOrder = 's-m-l';
      }
      const fixedSelect = widget.querySelector('select#product-4, select[name="properties[Product 4]"]');
      const fixedLabel = fixedSelect?.id
        ? widget.querySelector(`label[for="${CSS.escape(fixedSelect.id)}"]`)
        : widget.querySelector('label[for="product-4"]');
      if (fixedLabel) {
        fixedLabel.classList.add('bp-simple-bundles__fixed-field');
        fixedLabel.setAttribute('aria-hidden', 'true');
      }
      if (fixedSelect) {
        fixedSelect.classList.add('bp-simple-bundles__fixed-field');
        fixedSelect.tabIndex = -1;
        fixedSelect.setAttribute('aria-hidden', 'true');
      }
      if (fixedSelect && !widget.querySelector('[data-bp-bundle-included]')) {
        const included = document.createElement('p');
        included.className = 'bp-simple-bundles__included';
        included.dataset.bpBundleIncluded = '';
        included.textContent = root.dataset.bundleFixedItem || 'Included: Round Waste Bag Dispenser — Milk Brown';
        widget.append(included);
      }
      if (bundleRequired) {
        const configured = bundleChoiceNames.every(name => {
          const field = widget.querySelector(`[name="properties[${name}]"]`);
          return Boolean(field && field.value);
        });
        if (bundleSelectionSummary) {
          const selections = bundleChoiceNames.map(name => {
            const field = widget.querySelector(`[name="properties[${name}]"]`);
            return field?.value ? `${name}: ${field.value}` : '';
          }).filter(Boolean);
          setText(bundleSelectionSummary, selections.length ? `Selected — ${selections.join(' · ')}` : 'Choose a size and colours to see your selection.');
        }
        const choiceProductHandles = {
          'Harness Color': 'valley-harness-lead-set',
          'Treat Pouch Color': 'toast-treat-pouch',
          'Bowl Color': 'collapsible-silicone-travel-bowl'
        };
        const bundleMediaMap = root.querySelector('[data-bp-bundle-media-map]');
        Object.entries(choiceProductHandles).forEach(([name, componentHandle]) => {
          const field = widget.querySelector(`[name="properties[${name}]"]`);
          if (!field) return;
          let preview = widget.querySelector(`.bp-bundle-choice-preview[data-choice-name="${name}"]`);
          if (!preview) {
            preview = document.createElement('span');
            preview.className = 'bp-bundle-choice-preview';
            preview.dataset.choiceName = name;
            const fieldLabel = field.closest('label');
            (fieldLabel || field).insertAdjacentElement('afterend', preview);
          }
          const selectedValue = field.value || '';
          if (preview.dataset.choiceValue === selectedValue) return;
          preview.dataset.choiceValue = selectedValue;
          const matchingMedia = bundleMediaMap && [...bundleMediaMap.querySelectorAll('[data-bp-bundle-media]')]
            .find(item => item.dataset.componentHandle === componentHandle
              && item.dataset.choiceName === name
              && item.dataset.choiceValue === selectedValue);
          preview.replaceChildren();
          if (matchingMedia?.dataset.src) {
            const image = document.createElement('img');
            image.src = matchingMedia.dataset.thumbSrc || matchingMedia.dataset.src;
            image.alt = '';
            image.loading = 'lazy';
            const text = document.createElement('span');
            text.textContent = `Selected: ${selectedValue}`;
            preview.append(image, text);
          } else preview.textContent = selectedValue ? `Selected: ${selectedValue}` : 'Choose a colour.';
        });
        const appVariantId = widget.dataset.simpleBundlesVariantId;
        const appVariant = appVariantId && select ? [...select.options].find(option => option.value === appVariantId) : null;
        if (appVariant && select.value !== appVariant.value) select.value = appVariant.value;
        let appVariantMatches = Boolean(appVariant);
        if (!select && variantField) {
          appVariantMatches = Boolean(configured && serverVariantAvailable && appVariantId && appVariantId === serverVariantId);
          const nextVariantId = serverVariantId;
          if (variantField.value !== nextVariantId) variantField.value = nextVariantId;
        }
        bundleReady = configured && appVariantMatches;
        if (bundleReadiness) {
          setHidden(bundleReadiness, bundleReady);
          setText(bundleReadiness, bundleReady ? '' : (configured ? 'Preparing this set combination…' : 'Choose all four set options to continue.'));
        }
      }
    };
    if (form) {
      enhanceSimpleBundles();
      const bundleSelector = '#simple-bundles-io-options';
      const simpleBundlesObserver = new MutationObserver(mutations => {
        if (mutationsTouchSelector(mutations, bundleSelector)) syncVariant();
      });
      simpleBundlesObserver.observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-simple-bundles-variant-id', 'disabled', 'selected', 'value'] });
      signal.addEventListener('abort', () => simpleBundlesObserver.disconnect(), { once: true });
    }
    const syncVariant = () => {
      enhanceSimpleBundles();
      const option = select?.selectedOptions?.[0];
      if (select && variantField && variantField !== select && variantField.value !== select.value) variantField.value = select.value;
      if (price && option?.dataset.price) setText(price, option.dataset.price);
      if (stickyPrice && option?.dataset.price) setText(stickyPrice, option.dataset.price);
      const hasDiscount = Boolean(option?.dataset.comparePrice);
      if (comparePrice) {
        setText(comparePrice, option?.dataset.comparePrice || '');
        setClassState(comparePrice, 'is-hidden', !hasDiscount);
      }
      if (discount) {
        setText(discount, hasDiscount ? `${option.dataset.discountPercent}% OFF` : '');
        setClassState(discount, 'is-hidden', !hasDiscount);
      }
      addButtons.forEach(add => {
        if (add.dataset.enabled !== 'true') return;
        const variantAvailable = option ? option.dataset.available === 'true' : variantField?.dataset.available === 'true';
        const available = variantAvailable && bundleReady;
        const shouldDisable = !available;
        if (add.disabled !== shouldDisable) add.disabled = shouldDisable;
        setText(add, available ? add.dataset.addLabel : (bundleRequired && !bundleReady ? add.dataset.addLabel : add.dataset.soldLabel));
      });
      const variantAvailable = option ? option.dataset.available === 'true' : variantField?.dataset.available === 'true';
      return Boolean(variantAvailable && bundleReady);
    };
    select?.addEventListener('change', syncVariant, { signal });
    form?.addEventListener('change', event => {
      if (!event.target.matches('#simple-bundles-io-options select')) return;
      syncVariant();
      const choiceName = event.target.name?.match(/^properties\[(Harness Color|Treat Pouch Color|Bowl Color)\]$/)?.[1];
      if (!choiceName) return;
      const expectedHandles = {
        'Harness Color': 'valley-harness-lead-set',
        'Treat Pouch Color': 'toast-treat-pouch',
        'Bowl Color': 'collapsible-silicone-travel-bowl'
      };
      const mappedMedia = [...root.querySelectorAll('[data-bp-bundle-media]')].find(item => (
        item.dataset.componentHandle === expectedHandles[choiceName]
        && item.dataset.choiceName === choiceName
        && item.dataset.choiceValue === event.target.value
      ));
      if (!mappedMedia?.dataset.src) return;
      root.querySelector('[data-v2-primary-gallery]')?.dispatchEvent(new CustomEvent('bp:bundle-media-select', {
        detail: {
          src: mappedMedia.dataset.src,
          alt: `${mappedMedia.dataset.componentName} — ${mappedMedia.dataset.choiceValue}`,
          title: `${mappedMedia.dataset.componentName} — ${mappedMedia.dataset.choiceValue}`
        }
      }));
    }, { signal });
    syncVariant();

    if (sticky && window.matchMedia('(max-width: 767px)').matches && 'IntersectionObserver' in window) {
      const blockers = new Map();
      const updateSticky = () => setClassState(sticky, 'is-suppressed', [...blockers.values()].some(Boolean));
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => blockers.set(entry.target, entry.isIntersecting));
        updateSticky();
      }, { threshold: 0.08 });
      [...root.querySelectorAll('.bp-product-form__controls'), ...document.querySelectorAll('.site-footer')].forEach(element => {
        blockers.set(element, false);
        observer.observe(element);
      });
      const overlaySelector = '#shopify-pc__banner, .shopify-pc__banner__dialog, #ShopifyChat, [data-testid="consent-banner"], #preview-bar-iframe';
      const checkOverlays = () => {
        const overlay = document.querySelector(overlaySelector);
        blockers.set(document.body, Boolean(overlay && overlay.getClientRects().length));
        updateSticky();
      };
      const mutationObserver = new MutationObserver(mutations => {
        if (mutationsTouchSelector(mutations, overlaySelector)) checkOverlays();
      });
      mutationObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'style'] });
      checkOverlays();
      signal.addEventListener('abort', () => {
        observer.disconnect();
        mutationObserver.disconnect();
      }, { once: true });
    }

    if (!form || primaryAdd?.dataset.enabled !== 'true') return;
    form.addEventListener('submit', async event => {
      event.preventDefault();
      const message = root.querySelector('[data-form-message]');
      const button = event.submitter?.matches('[data-add-to-cart]') ? event.submitter : primaryAdd;
      if (!message || !button) return;
      if (!syncVariant()) {
        message.textContent = bundleRequired && !bundleReady ? 'Choose all four set options before adding to your bag.' : 'This option is currently unavailable.';
        message.className = 'bp-form-message is-error';
        return;
      }
      const formData = new FormData(form);
      if (bundleRequired && bundlePropertyNames.some(name => !formData.get(`properties[${name}]`))) {
        message.textContent = 'Set options could not be saved. Review all four choices and try again.';
        message.className = 'bp-form-message is-error';
        return;
      }
      addButtons.forEach(item => {
        item.disabled = true;
        item.setAttribute('aria-busy', 'true');
      });
      message.textContent = 'Adding…';
      message.className = 'bp-form-message';
      try {
        const response = await fetch(`${window.BogerPets.add}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
          signal
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || 'Unable to add this item. Try again.');
        message.textContent = 'Added to your bag.';
        message.classList.add('is-success');
        window.location.assign(window.BogerPets.cart);
      } catch (error) {
        if (error.name === 'AbortError') return;
        message.textContent = error.message || 'Unable to add this item. Try again.';
        message.classList.add('is-error');
      } finally {
        addButtons.forEach(item => item.removeAttribute('aria-busy'));
        syncVariant();
      }
    }, { signal });
  });
})();
